import { useEffect, useState } from "react";
import { Tag, Plus, Trash2, Copy, Percent, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";

const empty = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  min_order_amount: 0,
  max_discount: 0,
  valid_from: "",
  valid_to: "",
  usage_limit: 0,
  active: true,
  description: "",
};

export default function Coupons() {
  usePageTitle("Coupons");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/coupons");
      setRows(data);
    } catch (e) { toast.error("Could not load coupons"); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, code: form.code.trim().toUpperCase() };
      if (!payload.code) { toast.error("Code required"); return; }
      if (editing) {
        await api.put(`/coupons/${editing}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/coupons", payload);
        toast.success("Coupon created");
      }
      setForm(empty); setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      min_order_amount: c.min_order_amount || 0,
      max_discount: c.max_discount || 0,
      valid_from: c.valid_from || "",
      valid_to: c.valid_to || "",
      usage_limit: c.usage_limit || 0,
      active: c.active,
      description: c.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    try { await api.delete(`/coupons/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Tag className="text-[#FF6B00]" />
        <h1 className="text-2xl font-black tracking-tight">Coupons</h1>
      </div>

      <form onSubmit={submit} className="p-6 border-2 border-zinc-200 rounded-2xl bg-white space-y-4" data-testid="coupon-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Code*">
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="input" placeholder="WELCOME10" data-testid="coupon-code-input" />
          </Field>
          <Field label="Type">
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="input" data-testid="coupon-type">
              <option value="percent">Percent (%)</option>
              <option value="flat">Flat (₹)</option>
            </select>
          </Field>
          <Field label={form.discount_type === "percent" ? "Discount %" : "Discount ₹"}>
            <input type="number" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })} className="input" data-testid="coupon-value" />
          </Field>
          <Field label="Min Order ₹">
            <input type="number" min="0" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: parseFloat(e.target.value) || 0 })} className="input" />
          </Field>
          {form.discount_type === "percent" && (
            <Field label="Max Discount ₹ (cap)">
              <input type="number" min="0" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: parseFloat(e.target.value) || 0 })} className="input" placeholder="0 = no cap" />
            </Field>
          )}
          <Field label="Usage Limit">
            <input type="number" min="0" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: parseInt(e.target.value) || 0 })} className="input" placeholder="0 = unlimited" />
          </Field>
          <Field label="Valid From">
            <input type="datetime-local" value={form.valid_from ? form.valid_from.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="input" />
          </Field>
          <Field label="Valid To">
            <input type="datetime-local" value={form.valid_to ? form.valid_to.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, valid_to: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="input" />
          </Field>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span className="text-sm font-semibold">Active</span>
          </label>
        </div>
        <Field label="Description">
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" placeholder="Welcome offer for new customers" />
        </Field>
        <div className="flex items-center gap-2">
          <button type="submit" disabled={saving} className="btn-swiss btn-accent" data-testid="coupon-submit">
            <Plus size={14} className="mr-1" /> {editing ? "Update Coupon" : "Create Coupon"}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="btn-swiss btn-outline">Cancel</button>}
        </div>
      </form>

      {/* List */}
      <div className="border-2 border-zinc-200 rounded-2xl bg-white overflow-hidden">
        <div className="p-4 font-bold border-b border-zinc-100">Active Coupons ({rows.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Discount</th>
                <th className="text-left px-4 py-3">Min Order</th>
                <th className="text-left px-4 py-3">Uses</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="coupon-rows">
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-mono font-bold flex items-center gap-2">
                    {c.code} <button onClick={() => copy(c.code)}><Copy size={12} className="text-zinc-400 hover:text-[#FF6B00]" /></button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1">
                      {c.discount_type === "percent" ? <Percent size={12} /> : <IndianRupee size={12} />}
                      {c.discount_value}
                      {c.max_discount > 0 && <span className="text-[10px] text-zinc-500">(max ₹{c.max_discount})</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">₹{c.min_order_amount || 0}</td>
                  <td className="px-4 py-3">{c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => edit(c)} className="text-xs text-[#FF6B00] font-bold mr-3" data-testid={`edit-coupon-${c.id}`}>Edit</button>
                    <button onClick={() => del(c.id)} className="text-red-500 hover:text-red-700" data-testid={`del-coupon-${c.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-zinc-500">No coupons yet — create one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
